import { Ref } from 'vue';
import { watchArray } from '@vueuse/core';
import mergeWith from 'lodash/mergeWith';
import isEqual from 'lodash/isEqual';
import remove from 'lodash/remove';
import useMarkerLayer from './useMarkerLayer';
import usePolylineLayer from './usePolylineLayer';
import usePolygonLayer from './usePolygonLayer';
import useHeatLayer from './useHeatLayer';
import useModelLayer from './useModelLayer';
import useLabelLayer from './useLabelLayer';
import useHDRoadModelLayer from './useHDRoadModelLayer';
import useGridLayer from './useGridLayer';
import useRadiationLayer from './useRadiationLayer';
import useTrackLayer from './useTrackLayer';
import useTrafficSignalLayer from './useTrafficSignalLayer';
import useTrailLayer from './useTrailLayer';
import useBaseMapLayer from './useBaseMapLayer';
import useBaseServiceLayer from './useBaseServiceLayer';
import useDotLayer from './useDotLayer';
import useCircleLayer from './useCircleLayer';
import useMoveAlongLayer from './useMoveAlongLayer';

const LayerTypeHandlerMap = {
  marker: useMarkerLayer,
  polyline: usePolylineLayer,
  polygon: usePolygonLayer,
  heat: useHeatLayer,
  model: useModelLayer,
  label: useLabelLayer,
  HDRoadModel: useHDRoadModelLayer,
  radiation: useRadiationLayer,
  track: useTrackLayer,
  trail: useTrailLayer,
  dot: useDotLayer,
  trafficSignal: useTrafficSignalLayer,
  baseMap: useBaseMapLayer,
  baseService: useBaseServiceLayer,
  circle: useCircleLayer,
  grid: useGridLayer,
  moveAlong: useMoveAlongLayer,
};

export default function useLayerManager(mapInstance: LooseObject | null, layerList: Ref<MapLayer[]>): {
  clearAllLayers?: () => void
} {
  if (!mapInstance) return {};

  let layerMap = reactive<LooseObject>({});
  mapInstance.layerInstanceList = mapInstance.layerInstanceList || [];
  let { layerInstanceList } = mapInstance;

  const clearAllLayers = (): void => {
    layerInstanceList.forEach((instance: LooseObject) => {
      instance?.unmount();
    });
    layerInstanceList = [];
    mapInstance?.unWatchArray();
    mapInstance && (mapInstance.unWatchArray = null);
    layerMap = {};
  };


  const createLayer = async (layer: MapLayer) => {
    const { type } = layer;
    layerMap[layer.id] = layer;
    const layerInstance = await LayerTypeHandlerMap[type]?.(mapInstance, toRef(layerMap, layer.id));
    if (!layerInstance || typeof layerInstance !== 'object') return;
    layerInstance.$id = layer.id;
    layerInstanceList.push(layerInstance);
  };


  const clearLayersByIds = (ids: string[]): void => {
    remove(layerInstanceList, (layerInstance: LooseObject) => {
      if (ids.indexOf(layerInstance.$id) !== -1) {
        delete layerMap[layerInstance.$id];
        layerInstance?.unmount();
        return true;
      }
      return false;
    });
  };


  if (!mapInstance.unWatchArray) {
    mapInstance.unWatchArray = watchArray(
      layerList,
      (newList: MapLayer[], oldList: MapLayer[], added: MapLayer[], removed: MapLayer[]) => {
        if (added?.length) {
          added.forEach((layer: MapLayer) => {
            // 改变图层某个属性值也会变为added，此时需和原列表对比id判断是否新加的，还是仅属性改变的
            if (oldList.map(item => item.id).indexOf(layer.id) !== -1) {
              mergeWith(layerMap[layer.id], layer, (objValue, srcValue) => {
                // 当数据类型为数组且值变化时，直接使用新的数组
                if (Array.isArray(objValue) && !isEqual(objValue, srcValue)) {
                  return srcValue;
                }
              });
              return;
            }
            createLayer(layer);
          });
        }
        if (removed?.length) {
          const removeIds: string[] = [];
          removed.forEach((removeItem) => {
            if (newList.map(item => item.id).indexOf(removeItem.id) !== -1) return;
            removeIds.push(removeItem.id);
          });
          clearLayersByIds(removeIds);
        }
      }, { deep: true },
    );
  }

  // 如果新建过图层则不会重复创建图层，有watchArray中逻辑处理图层的变化
  if (!layerInstanceList.length) {
    layerList.value.forEach((layer: MapLayer) => {
      createLayer(layer);
    });
  }

  return {
    clearAllLayers,
  };
}
